import { useState, useMemo } from "react";

interface PlateCode {
  code: string;
  location: string;
}

interface PlateFormat {
  example: string;
  legend: Array<{ color: string; label: string }>;
}

interface Country {
  name: string;
  countryCode: string;
  codes: PlateCode[];
  format?: PlateFormat;
}

interface Region {
  name: string;
  countries: Country[];
}

// Mapping de països a banderes
const countryFlags: Record<string, string> = {
  "Estats Units": "🇺🇸",
  "Canadà": "🇨🇦",
  "Mèxic": "🇲🇽",
  "Alemanya": "🇩🇪",
  "Espanya": "🇪🇸",
  "França": "🇫🇷",
  "Itàlia": "🇮🇹",
  "Suïssa": "🇨🇭",
  "Regne Unit": "🇬🇧",
  "Països Baixos": "🇳🇱",
  "Bèlgica": "🇧🇪",
  "Portugal": "🇵🇹",
  "Albània": "🇦🇱",
  "Andorra": "🇦🇩",
  "Àustria": "🇦🇹",
  "Bielorússia": "🇧🇾",
  "Bòsnia i Hercegovina": "🇧🇦",
  "Bulgària": "🇧🇬",
  "Croàcia": "🇭🇷",
  "Xipre": "🇨🇾",
  "República Txeca": "🇨🇿",
  "Dinamarca": "🇩🇰",
  "Estònia": "🇪🇪",
  "Finlàndia": "🇫🇮",
  "Grècia": "🇬🇷",
  "Hongria": "🇭🇺",
  "Islàndia": "🇮🇸",
  "Irlanda": "🇮🇪",
  "Letònia": "🇱🇻",
  "Liechtenstein": "🇱🇮",
  "Lituània": "🇱🇹",
  "Luxemburg": "🇱🇺",
  "Malta": "🇲🇹",
  "Moldàvia": "🇲🇩",
  "Mònaco": "🇲🇨",
  "Montenegro": "🇲🇪",
  "Macedònia del Nord": "🇲🇰",
  "Noruega": "🇳🇴",
  "Polònia": "🇵🇱",
  "Romania": "🇷🇴",
  "San Marino": "🇸🇲",
  "Sèrbia": "🇷🇸",
  "Eslovàquia": "🇸🇰",
  "Eslovènia": "🇸🇮",
  "Suècia": "🇸🇪",
  "Ucraïna": "🇺🇦",
  "Ciutat del Vaticà": "🇻🇦",
  "Japó": "🇯🇵",
  "Xina": "🇨🇳",
  "Argentina": "🇦🇷",
  "Brasil": "🇧🇷",
  "Xile": "🇨🇱",
  "Colòmbia": "🇨🇴",
  "Austràlia": "🇦🇺",
  "Nova Zelanda": "🇳🇿",
  "Sud-àfrica": "🇿🇦",
  "Emirats Àrabs Units": "🇦🇪",
};

const getCountryFlag = (countryName: string): string => {
  return countryFlags[countryName] || "🏳️";
};

// Països de la Unió Europea i les seves lletres a la banda blava
const euCountries: Record<string, string> = {
  "Espanya": "E",
  "Alemanya": "D",
  "França": "F",
  "Itàlia": "I",
  "Bèlgica": "B",
  "Països Baixos": "NL",
  "Portugal": "P",
  "Albània": "AL",
  "Àustria": "A",
  "Bòsnia i Hercegovina": "BIH",
  "Bulgària": "BG",
  "Croàcia": "HR",
  "Xipre": "CY",
  "República Txeca": "CZ",
  "Dinamarca": "DK",
  "Estònia": "EST",
  "Finlàndia": "FIN",
  "Grècia": "GR",
  "Hongria": "H",
  "Irlanda": "IRL",
  "Letònia": "LV",
  "Lituània": "LT",
  "Luxemburg": "L",
  "Malta": "M",
  "Moldàvia": "MD",
  "Montenegro": "MNE",
  "Macedònia del Nord": "NMK",
  "Polònia": "PL",
  "Romania": "RO",
  "Sèrbia": "SRB",
  "Eslovàquia": "SK",
  "Eslovènia": "SLO",
  "Suècia": "S",
};

const isEUCountry = (countryName: string): boolean => {
  return countryName in euCountries;
};

// Formats de matrícula per país amb parts destacades
const plateFormats: Record<string, PlateFormat> = {
  "Espanya": {
    example: "EU|1234|ABC",
    legend: [
      { color: "bg-blue-600 text-white", label: "E: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "1234: Números" },
      { color: "bg-gray-100 text-gray-800", label: "ABC: Lletres" }
    ]
  },
  "Estats Units": {
    example: "ABC|1234",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "ABC 1234: Lletres i números" }
    ]
  },
  "Canadà": {
    example: "ABC|123",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "ABC 123: Lletres i números" }
    ]
  },
  "Alemanya": {
    example: "EU|B|AB|1234",
    legend: [
      { color: "bg-blue-600 text-white", label: "D: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "B: Codi ciutat" },
      { color: "bg-gray-100 text-gray-800", label: "AB: Lletres" },
      { color: "bg-gray-100 text-gray-800", label: "1234: Números" }
    ]
  },
  "França": {
    example: "EU|75|AB|123|CD",
    legend: [
      { color: "bg-blue-600 text-white", label: "F: Banda blava UE" },
      { color: "bg-blue-100 text-blue-800", label: "75: Codi departament" },
      { color: "bg-gray-100 text-gray-800", label: "AB 123 CD: Lletres i números" }
    ]
  },
  "Itàlia": {
    example: "EU|RM|AB 123 CD",
    legend: [
      { color: "bg-blue-600 text-white", label: "I: Banda blava UE" },
      { color: "bg-green-100 text-green-800", label: "RM: Codi província" },
      { color: "bg-gray-100 text-gray-800", label: "AB 123 CD: Lletres i números" }
    ]
  },
  "Suïssa": {
    example: "ZH|AB|1234",
    legend: [
      { color: "bg-red-100 text-red-800", label: "ZH: Codi cantó" },
      { color: "bg-gray-100 text-gray-800", label: "AB: Lletres" },
      { color: "bg-gray-100 text-gray-800", label: "1234: Números" }
    ]
  },
  "Japó": {
    example: "T|あ1234",
    legend: [
      { color: "bg-red-100 text-red-800", label: "T: Codi prefectura" },
      { color: "bg-gray-100 text-gray-800", label: "あ1234: Kana i números" }
    ]
  },
  "Xina": {
    example: "京|A·12345",
    legend: [
      { color: "bg-red-100 text-red-800", label: "京: Codi província" },
      { color: "bg-gray-100 text-gray-800", label: "A·12345: Lletres i números" }
    ]
  },
  "Argentina": {
    example: "C|ABC 123",
    legend: [
      { color: "bg-blue-100 text-blue-800", label: "C: Codi província" },
      { color: "bg-gray-100 text-gray-800", label: "ABC 123: Lletres i números" }
    ]
  },
  "Brasil": {
    example: "SP|ABC|1234",
    legend: [
      { color: "bg-green-100 text-green-800", label: "SP: Codi estat" },
      { color: "bg-gray-100 text-gray-800", label: "ABC 1234: Lletres i números" }
    ]
  },
  "Austràlia": {
    example: "NSW|ABC|123",
    legend: [
      { color: "bg-blue-100 text-blue-800", label: "NSW: Codi estat" },
      { color: "bg-gray-100 text-gray-800", label: "ABC 123: Lletres i números" }
    ]
  },
  "Sud-àfrica": {
    example: "ABC 123|GP",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "ABC 123: Lletres i números" },
      { color: "bg-yellow-100 text-yellow-800", label: "GP: Codi província" }
    ]
  },
  "Emirats Àrabs Units": {
    example: "DXB|12345",
    legend: [
      { color: "bg-red-100 text-red-800", label: "DXB: Codi emirat" },
      { color: "bg-gray-100 text-gray-800", label: "12345: Números" }
    ]
  },
  "Regne Unit": {
    example: "AA|11|AAA",
    legend: [
      { color: "bg-purple-100 text-purple-800", label: "AA: Regió/àrea de registre" },
      { color: "bg-gray-100 text-gray-800", label: "11: Any (dos últims dígits)" },
      { color: "bg-gray-100 text-gray-800", label: "AAA: Lletres seqüencials" }
    ]
  },
  "Països Baixos": {
    example: "EU|A|001|AA",
    legend: [
      { color: "bg-blue-600 text-white", label: "NL: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "A 001 AA: Combinació seqüencial" }
    ]
  },
  "Bèlgica": {
    example: "EU|1|ABC|234",
    legend: [
      { color: "bg-blue-600 text-white", label: "B: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "1 ABC 234: Dígit, lletres i números" }
    ]
  },
  "Portugal": {
    example: "EU|AB|12|CD",
    legend: [
      { color: "bg-blue-600 text-white", label: "P: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "AB 12 CD: Combinació seqüencial" }
    ]
  },
  "Mèxic": {
    example: "ABC|1234",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "ABC 1234: Format variable per estat (no homogeni)" }
    ]
  },
  "Xile": {
    example: "AA|·1234",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "AA: Dues lletres" },
      { color: "bg-gray-100 text-gray-800", label: "1234: Quatre números" }
    ]
  },
  "Colòmbia": {
    example: "ABC|·123",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "ABC: Tres lletres" },
      { color: "bg-gray-100 text-gray-800", label: "123: Tres números" }
    ]
  },
  "Nova Zelanda": {
    example: "AAA|123",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "AAA: Tres lletres" },
      { color: "bg-gray-100 text-gray-800", label: "123: Tres números" }
    ]
  },
  "Albània": {
    example: "EU|AB|123|CD",
    legend: [
      { color: "bg-blue-600 text-white", label: "AL: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "AB 123 CD: Lletres i números" }
    ]
  },
  "Andorra": {
    example: "L|1234",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "L: Lletra" },
      { color: "bg-gray-100 text-gray-800", label: "1234: Números" }
    ]
  },
  "Àustria": {
    example: "EU|W|123|AB",
    legend: [
      { color: "bg-blue-600 text-white", label: "A: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "W: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "123 AB: Números i lletres" }
    ]
  },
  "Bielorússia": {
    example: "1234|AB|7",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "1234 AB: Números i lletres" },
      { color: "bg-red-100 text-red-800", label: "7: Codi regió (últim dígit)" }
    ]
  },
  "Bòsnia i Hercegovina": {
    example: "EU|A12|B|345",
    legend: [
      { color: "bg-blue-600 text-white", label: "BIH: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "A12 B 345: Format específic" }
    ]
  },
  "Bulgària": {
    example: "EU|CA|1234|BC",
    legend: [
      { color: "bg-blue-600 text-white", label: "BG: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "CA: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "1234 BC: Números i lletres" }
    ]
  },
  "Croàcia": {
    example: "EU|ZG|123|AB",
    legend: [
      { color: "bg-blue-600 text-white", label: "HR: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "ZG: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "123 AB: Números i lletres" }
    ]
  },
  "Xipre": {
    example: "EU|ABC|123",
    legend: [
      { color: "bg-blue-600 text-white", label: "CY: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "ABC 123: Lletres i números" }
    ]
  },
  "República Txeca": {
    example: "EU|1A2|3456",
    legend: [
      { color: "bg-blue-600 text-white", label: "CZ: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "A: Codi regió (posició 2)" },
      { color: "bg-gray-100 text-gray-800", label: "1A2 3456: Format específic" }
    ]
  },
  "Dinamarca": {
    example: "AB|12|345",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "AB 12 345: Lletres i números" }
    ]
  },
  "Estònia": {
    example: "EU|123|ABC",
    legend: [
      { color: "bg-blue-600 text-white", label: "EST: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "123 ABC: Números i lletres" }
    ]
  },
  "Finlàndia": {
    example: "EU|ABC|123",
    legend: [
      { color: "bg-blue-600 text-white", label: "FIN: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "ABC 123: Lletres i números" }
    ]
  },
  "Grècia": {
    example: "EU|AKE|1234",
    legend: [
      { color: "bg-blue-600 text-white", label: "GR: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "AKE: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "1234: Números" }
    ]
  },
  "Hongria": {
    example: "EU|ABCD|123",
    legend: [
      { color: "bg-blue-600 text-white", label: "H: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "ABCD 123: Lletres i números" }
    ]
  },
  "Islàndia": {
    example: "ABC12",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "ABC12: Lletres i números" }
    ]
  },
  "Irlanda": {
    example: "EU|23|D|12345",
    legend: [
      { color: "bg-blue-600 text-white", label: "IRL: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "23 D: Codi comtat" },
      { color: "bg-gray-100 text-gray-800", label: "12345: Números" }
    ]
  },
  "Letònia": {
    example: "EU|AB|1234",
    legend: [
      { color: "bg-blue-600 text-white", label: "LV: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "AB 1234: Lletres i números" }
    ]
  },
  "Liechtenstein": {
    example: "FL|1234",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "FL 1234: Format específic" }
    ]
  },
  "Lituània": {
    example: "EU|ABC|123",
    legend: [
      { color: "bg-blue-600 text-white", label: "LT: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "ABC 123: Lletres i números" }
    ]
  },
  "Luxemburg": {
    example: "EU|AB|1234",
    legend: [
      { color: "bg-blue-600 text-white", label: "L: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "AB 1234: Lletres i números" }
    ]
  },
  "Malta": {
    example: "EU|ABC|123",
    legend: [
      { color: "bg-blue-600 text-white", label: "M: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "ABC 123: Lletres i números" }
    ]
  },
  "Moldàvia": {
    example: "ABC|123",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "ABC 123: Lletres i números" }
    ]
  },
  "Mònaco": {
    example: "A|123",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "A 123: Lletra i números" }
    ]
  },
  "Montenegro": {
    example: "EU|PG|123",
    legend: [
      { color: "bg-blue-600 text-white", label: "MNE: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "PG: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "123: Números" }
    ]
  },
  "Macedònia del Nord": {
    example: "EU|SK|1234|AB",
    legend: [
      { color: "bg-blue-600 text-white", label: "NMK: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "SK: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "1234 AB: Números i lletres" }
    ]
  },
  "Noruega": {
    example: "AB|12345",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "AB 12345: Lletres i números" }
    ]
  },
  "Polònia": {
    example: "EU|KR|1234Z",
    legend: [
      { color: "bg-blue-600 text-white", label: "PL: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "KR: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "1234Z: Números i lletra" }
    ]
  },
  "Romania": {
    example: "EU|AB|12|XYZ",
    legend: [
      { color: "bg-blue-600 text-white", label: "RO: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "AB: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "12 XYZ: Números i lletres" }
    ]
  },
  "San Marino": {
    example: "M|1234",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "M 1234: Lletra i números" }
    ]
  },
  "Sèrbia": {
    example: "EU|BG|123|AB",
    legend: [
      { color: "bg-blue-600 text-white", label: "SRB: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "BG: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "123 AB: Números i lletres" }
    ]
  },
  "Eslovàquia": {
    example: "EU|BA|123XY",
    legend: [
      { color: "bg-blue-600 text-white", label: "SK: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "BA: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "123XY: Números i lletres" }
    ]
  },
  "Eslovènia": {
    example: "EU|LJ|234|BC",
    legend: [
      { color: "bg-blue-600 text-white", label: "SLO: Banda blava UE" },
      { color: "bg-yellow-100 text-yellow-800", label: "LJ: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "234 BC: Números i lletres" }
    ]
  },
  "Suècia": {
    example: "EU|ABC|123",
    legend: [
      { color: "bg-blue-600 text-white", label: "S: Banda blava UE" },
      { color: "bg-gray-100 text-gray-800", label: "ABC 123: Lletres i números" }
    ]
  },
  "Ucraïna": {
    example: "AA|1234|BB",
    legend: [
      { color: "bg-yellow-100 text-yellow-800", label: "AA: Codi regió" },
      { color: "bg-gray-100 text-gray-800", label: "1234 BB: Números i lletres" }
    ]
  },
  "Ciutat del Vaticà": {
    example: "SCV|1",
    legend: [
      { color: "bg-gray-100 text-gray-800", label: "SCV 1 / CV 123: Format específic" }
    ]
  }
};

const licensePlateData: Region[] = [
  {
    name: "Amèrica del Nord",
    countries: [
      {
        name: "Estats Units",
        countryCode: "USA",
        codes: []
      },
      {
        name: "Canadà",
        countryCode: "CA",
        codes: []
      },
      {
        name: "Mèxic",
        countryCode: "MEX",
        codes: []
      }
    ]
  },
  {
    name: "Europa",
    countries: [
      {
        name: "Albània",
        countryCode: "AL",
        codes: []
      },
      {
        name: "Alemanya",
        countryCode: "D",
        codes: [
          { code: "B", location: "Berlín" },
          { code: "M", location: "Munic" },
          { code: "F", location: "Frankfurt" },
          { code: "HH", location: "Hamburg" },
          { code: "K", location: "Colònia" },
          { code: "S", location: "Stuttgart" },
          { code: "E", location: "Essen" },
          { code: "D", location: "Düsseldorf" },
          { code: "DO", location: "Dortmund" },
          { code: "L", location: "Leipzig" }
        ]
      },
      {
        name: "Andorra",
        countryCode: "AND",
        codes: []
      },
      {
        name: "Àustria",
        countryCode: "A",
        codes: [
          { code: "W", location: "Viena" },
          { code: "S", location: "Salzburg" },
          { code: "K", location: "Caríntia" },
          { code: "O", location: "Alta Àustria" },
          { code: "ST", location: "Estíria" },
          { code: "T", location: "Tirol" },
          { code: "V", location: "Vorarlberg" },
          { code: "L", location: "Linz" },
          { code: "E", location: "Eisenstadt" },
          { code: "G", location: "Graz" }
        ]
      },
      {
        name: "Bèlgica",
        countryCode: "B",
        codes: []
      },
      {
        name: "Bielorússia",
        countryCode: "BY",
        codes: []
      },
      {
        name: "Bòsnia i Hercegovina",
        countryCode: "BIH",
        codes: []
      },
      {
        name: "Bulgària",
        countryCode: "BG",
        codes: [
          { code: "CA", location: "Sofia" },
          { code: "PB", location: "Plovdiv" },
          { code: "V", location: "Varna" },
          { code: "B", location: "Burgas" },
          { code: "R", location: "Ruse" },
          { code: "H", location: "Shumen" },
          { code: "PP", location: "Pleven" },
          { code: "A", location: "Blagoevgrad" },
          { code: "BT", location: "Veliko Tarnovo" },
          { code: "E", location: "Stara Zagora" }
        ]
      },
      {
        name: "Ciutat del Vaticà",
        countryCode: "V",
        codes: []
      },
      {
        name: "Croàcia",
        countryCode: "HR",
        codes: [
          { code: "ZG", location: "Zagreb" },
          { code: "SP", location: "Split" },
          { code: "RI", location: "Rijeka" },
          { code: "OS", location: "Osijek" },
          { code: "ZD", location: "Zadar" },
          { code: "ST", location: "Šibenik" },
          { code: "PU", location: "Pula" },
          { code: "DU", location: "Dubrovnik" },
          { code: "VZ", location: "Varaždin" },
          { code: "BJ", location: "Bjelovar" }
        ]
      },
      {
        name: "Xipre",
        countryCode: "CY",
        codes: []
      },
      {
        name: "República Txeca",
        countryCode: "CZ",
        codes: [
          { code: "A", location: "Praga" },
          { code: "B", location: "Brno" },
          { code: "C", location: "České Budějovice" },
          { code: "E", location: "Pardubice" },
          { code: "H", location: "Hradec Králové" },
          { code: "J", location: "Jihlava" },
          { code: "K", location: "Karlovy Vary" },
          { code: "L", location: "Liberec" },
          { code: "M", location: "Ostrava" },
          { code: "P", location: "Plzeň" }
        ]
      },
      {
        name: "Dinamarca",
        countryCode: "DK",
        codes: []
      },
      {
        name: "Espanya",
        countryCode: "E",
        codes: []
      },
      {
        name: "Estònia",
        countryCode: "EST",
        codes: []
      },
      {
        name: "Finlàndia",
        countryCode: "FIN",
        codes: []
      },
      {
        name: "França",
        countryCode: "F",
        codes: [
          { code: "75", location: "París" },
          { code: "69", location: "Lió" },
          { code: "13", location: "Marsella" },
          { code: "31", location: "Tolosa" },
          { code: "33", location: "Bordeus" },
          { code: "59", location: "Lille" },
          { code: "67", location: "Estrasburg" },
          { code: "34", location: "Montpeller" },
          { code: "44", location: "Nantes" },
          { code: "38", location: "Grenoble" }
        ]
      },
      {
        name: "Itàlia",
        countryCode: "I",
        codes: [
          { code: "RM", location: "Roma" },
          { code: "MI", location: "Milà" },
          { code: "NA", location: "Nàpols" },
          { code: "TO", location: "Torí" },
          { code: "PA", location: "Palerm" },
          { code: "GE", location: "Gènova" },
          { code: "BO", location: "Bolonya" },
          { code: "FI", location: "Florència" },
          { code: "VE", location: "Venècia" },
          { code: "BA", location: "Bari" }
        ]
      },
      {
        name: "Hongria",
        countryCode: "H",
        codes: []
      },
      {
        name: "Irlanda",
        countryCode: "IRL",
        codes: [
          { code: "D", location: "Dublín" },
          { code: "C", location: "Cork" },
          { code: "G", location: "Galway" },
          { code: "L", location: "Limerick" },
          { code: "W", location: "Waterford" },
          { code: "KY", location: "Kerry" },
          { code: "KE", location: "Kildare" },
          { code: "KK", location: "Kilkenny" },
          { code: "LS", location: "Laois" },
          { code: "LD", location: "Longford" }
        ]
      },
      {
        name: "Islàndia",
        countryCode: "IS",
        codes: []
      },
      {
        name: "Grècia",
        countryCode: "GR",
        codes: [
          { code: "AKE", location: "Atenes" },
          { code: "B", location: "Salònica" },
          { code: "C", location: "Patres" },
          { code: "D", location: "Larissa" },
          { code: "E", location: "Volos" },
          { code: "F", location: "Creta" },
          { code: "G", location: "Kavala" },
          { code: "H", location: "Ioànnina" },
          { code: "K", location: "Kozani" },
          { code: "L", location: "Mitilene" }
        ]
      },
      {
        name: "Letònia",
        countryCode: "LV",
        codes: []
      },
      {
        name: "Liechtenstein",
        countryCode: "FL",
        codes: []
      },
      {
        name: "Lituània",
        countryCode: "LT",
        codes: []
      },
      {
        name: "Luxemburg",
        countryCode: "L",
        codes: []
      },
      {
        name: "Macedònia del Nord",
        countryCode: "NMK",
        codes: [
          { code: "SK", location: "Skopje" },
          { code: "BT", location: "Bitola" },
          { code: "KU", location: "Kumanovo" },
          { code: "PR", location: "Prilep" },
          { code: "TE", location: "Tetovo" },
          { code: "VE", location: "Veles" },
          { code: "ST", location: "Štip" },
          { code: "OH", location: "Ohrid" },
          { code: "GV", location: "Gostivar" },
          { code: "KA", location: "Kavadarci" }
        ]
      },
      {
        name: "Malta",
        countryCode: "M",
        codes: []
      },
      {
        name: "Moldàvia",
        countryCode: "MD",
        codes: []
      },
      {
        name: "Mònaco",
        countryCode: "MC",
        codes: []
      },
      {
        name: "Montenegro",
        countryCode: "MNE",
        codes: [
          { code: "PG", location: "Podgorica" },
          { code: "NK", location: "Nikšić" },
          { code: "PL", location: "Pljevlja" },
          { code: "CT", location: "Cetinje" },
          { code: "BR", location: "Bar" },
          { code: "BD", location: "Budva" },
          { code: "KL", location: "Kotor" },
          { code: "TV", location: "Tivat" },
          { code: "HN", location: "Herceg Novi" },
          { code: "BP", location: "Bijelo Polje" }
        ]
      },
      {
        name: "Noruega",
        countryCode: "N",
        codes: []
      },
      {
        name: "Països Baixos",
        countryCode: "NL",
        codes: []
      },
      {
        name: "Polònia",
        countryCode: "PL",
        codes: [
          { code: "KR", location: "Cracòvia" },
          { code: "PO", location: "Poznań" },
          { code: "WA", location: "Varsòvia" },
          { code: "GD", location: "Gdańsk" },
          { code: "WX", location: "Wrocław" },
          { code: "SL", location: "Silesia" },
          { code: "LU", location: "Lublin" },
          { code: "ZG", location: "Zielona Góra" },
          { code: "OL", location: "Olsztyn" },
          { code: "SZ", location: "Szczecin" }
        ]
      },
      {
        name: "Portugal",
        countryCode: "P",
        codes: []
      },
      {
        name: "Regne Unit",
        countryCode: "GB",
        codes: [
          { code: "A*", location: "Anglia (Peterborough / Norwich / Ipswich)" },
          { code: "B*", location: "Birmingham" },
          { code: "C*", location: "Cymru (Gal·les)" },
          { code: "D*", location: "Deeside / Shrewsbury / Chester" },
          { code: "E*", location: "Essex" },
          { code: "H*", location: "Hampshire i Dorset" },
          { code: "L*", location: "Londres" },
          { code: "M*", location: "Manchester i Merseyside" },
          { code: "N*", location: "North (Nord Anglaterra)" },
          { code: "S*", location: "Scotland" },
          { code: "Y*", location: "Yorkshire" }
        ]
      },
      {
        name: "Romania",
        countryCode: "RO",
        codes: [
          { code: "AB", location: "Alba" },
          { code: "AR", location: "Arad" },
          { code: "AG", location: "Argeș" },
          { code: "BC", location: "Bacău" },
          { code: "BH", location: "Bihor" },
          { code: "BN", location: "Bistrița-Năsăud" },
          { code: "BT", location: "Botoșani" },
          { code: "BR", location: "Brăila" },
          { code: "BV", location: "Brașov" },
          { code: "B", location: "Bucarest" }
        ]
      },
      {
        name: "San Marino",
        countryCode: "RSM",
        codes: []
      },
      {
        name: "Sèrbia",
        countryCode: "SRB",
        codes: [
          { code: "BG", location: "Belgrad" },
          { code: "NS", location: "Novi Sad" },
          { code: "NI", location: "Niš" },
          { code: "KG", location: "Kragujevac" },
          { code: "SU", location: "Subotica" },
          { code: "ZR", location: "Zrenjanin" },
          { code: "PZ", location: "Pančevo" },
          { code: "SM", location: "Smederevo" },
          { code: "JA", location: "Jagodina" },
          { code: "ČA", location: "Čačak" }
        ]
      },
      {
        name: "Eslovàquia",
        countryCode: "SK",
        codes: [
          { code: "BA", location: "Bratislava" },
          { code: "KE", location: "Košice" },
          { code: "PO", location: "Prešov" },
          { code: "ZA", location: "Žilina" },
          { code: "BB", location: "Banská Bystrica" },
          { code: "TT", location: "Trnava" },
          { code: "TN", location: "Trenčín" },
          { code: "NR", location: "Nitra" },
          { code: "ZV", location: "Zvolen" },
          { code: "PK", location: "Prievidza" }
        ]
      },
      {
        name: "Eslovènia",
        countryCode: "SLO",
        codes: [
          { code: "LJ", location: "Ljubljana" },
          { code: "MB", location: "Maribor" },
          { code: "CE", location: "Celje" },
          { code: "KR", location: "Kranj" },
          { code: "NM", location: "Novo Mesto" },
          { code: "KP", location: "Koper" },
          { code: "GO", location: "Nova Gorica" },
          { code: "MS", location: "Murska Sobota" },
          { code: "PO", location: "Postojna" },
          { code: "SG", location: "Slovenj Gradec" }
        ]
      },
      {
        name: "Suècia",
        countryCode: "S",
        codes: []
      },
      {
        name: "Suïssa",
        countryCode: "CH",
        codes: [
          { code: "ZH", location: "Zuric" },
          { code: "GE", location: "Ginebra" },
          { code: "VD", location: "Vaud" },
          { code: "BE", location: "Berna" },
          { code: "LU", location: "Lucerna" },
          { code: "TI", location: "Ticino" },
          { code: "BS", location: "Basilea" },
          { code: "SG", location: "St. Gallen" },
          { code: "FR", location: "Friburg" },
          { code: "VS", location: "Valais" }
        ]
      },
      {
        name: "Ucraïna",
        countryCode: "UA",
        codes: [
          { code: "AA", location: "Kiev" },
          { code: "AB", location: "Vínnytsia" },
          { code: "AC", location: "Volínia" },
          { code: "AE", location: "Dnipropetrovsk" },
          { code: "AH", location: "Donetsk" },
          { code: "AI", location: "Zhytomyr" },
          { code: "AK", location: "Zakarpattia" },
          { code: "AM", location: "Zaporizhzhia" },
          { code: "AO", location: "Ivano-Frankivsk" },
          { code: "AP", location: "Kirovohrad" }
        ]
      }
    ]
  },
  {
    name: "Àsia",
    countries: [
      {
        name: "Japó",
        countryCode: "J",
        codes: [
          { code: "T", location: "Tòquio" },
          { code: "O", location: "Osaka" },
          { code: "K", location: "Kyoto" },
          { code: "S", location: "Sapporo" },
          { code: "H", location: "Hiroshima" },
          { code: "F", location: "Fukuoka" },
          { code: "N", location: "Nagoya" },
          { code: "K", location: "Kobe" },
          { code: "Y", location: "Yokohama" }
        ]
      },
      {
        name: "Xina",
        countryCode: "CN",
        codes: [
          { code: "BJ", location: "Beijing" },
          { code: "SH", location: "Xangai" },
          { code: "GZ", location: "Guangzhou" },
          { code: "SZ", location: "Shenzhen" },
          { code: "CD", location: "Chengdu" },
          { code: "WH", location: "Wuhan" },
          { code: "TJ", location: "Tianjin" },
          { code: "CQ", location: "Chongqing" },
          { code: "HZ", location: "Hangzhou" },
          { code: "NJ", location: "Nanjing" }
        ]
      }
    ]
  },
  {
    name: "Amèrica del Sud",
    countries: [
      {
        name: "Argentina",
        countryCode: "RA",
        codes: [
          { code: "C", location: "Buenos Aires" },
          { code: "R", location: "Rosario" },
          { code: "M", location: "Mendoza" },
          { code: "L", location: "La Plata" },
          { code: "C", location: "Córdoba" },
          { code: "S", location: "Salta" },
          { code: "N", location: "Neuquén" },
          { code: "B", location: "Bariloche" },
          { code: "T", location: "Tucumán" },
          { code: "F", location: "Formosa" }
        ]
      },
      {
        name: "Brasil",
        countryCode: "BR",
        codes: [
          { code: "SP", location: "São Paulo" },
          { code: "RJ", location: "Rio de Janeiro" },
          { code: "BH", location: "Belo Horizonte" },
          { code: "POA", location: "Porto Alegre" },
          { code: "SSA", location: "Salvador" },
          { code: "CWB", location: "Curitiba" },
          { code: "FOR", location: "Fortaleza" },
          { code: "REC", location: "Recife" },
          { code: "BSB", location: "Brasília" },
          { code: "BEL", location: "Belém" }
        ]
      },
      {
        name: "Xile",
        countryCode: "RCH",
        codes: []
      },
      {
        name: "Colòmbia",
        countryCode: "CO",
        codes: []
      }
    ]
  },
  {
    name: "Oceania",
    countries: [
      {
        name: "Austràlia",
        countryCode: "AUS",
        codes: [
          { code: "NSW", location: "Nova Gal·les del Sud" },
          { code: "VIC", location: "Victòria" },
          { code: "QLD", location: "Queensland" },
          { code: "WA", location: "Austràlia Occidental" },
          { code: "SA", location: "Austràlia Meridional" },
          { code: "TAS", location: "Tasmània" },
          { code: "NT", location: "Territori del Nord" },
          { code: "ACT", location: "Territori de la Capital Australiana" }
        ]
      },
      {
        name: "Nova Zelanda",
        countryCode: "NZ",
        codes: []
      }
    ]
  },
  {
    name: "Àfrica",
    countries: [
      {
        name: "Sud-àfrica",
        countryCode: "ZA",
        codes: [
          { code: "GP", location: "Gauteng" },
          { code: "WC", location: "Cap Occidental" },
          { code: "EC", location: "Cap Oriental" },
          { code: "KZN", location: "KwaZulu-Natal" },
          { code: "MP", location: "Mpumalanga" },
          { code: "FS", location: "Estat Lliure" },
          { code: "LP", location: "Limpopo" },
          { code: "NW", location: "Nord-oest" },
          { code: "NC", location: "Cap del Nord" }
        ]
      }
    ]
  },
  {
    name: "Orient Mitjà",
    countries: [
      {
        name: "Emirats Àrabs Units",
        countryCode: "UAE",
        codes: [
          { code: "DXB", location: "Dubai" },
          { code: "AUH", location: "Abu Dhabi" },
          { code: "SHJ", location: "Sharjah" },
          { code: "AJM", location: "Ajman" },
          { code: "FUJ", location: "Fujairah" },
          { code: "RAK", location: "Ras al-Khaimah" },
          { code: "UAQ", location: "Umm al-Quwain" }
        ]
      }
    ]
  }
];

export const LicensePlates = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  // Normalize string for better search (remove accents, lowercase)
  const normalizeString = (str: string): string => {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  };

  // Filter data based on search and region
  const filteredData = useMemo(() => {
    let filtered = licensePlateData;

    // Filter by region
    if (selectedRegion) {
      filtered = filtered.filter(region => region.name === selectedRegion);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const normalizedQuery = normalizeString(searchQuery);
      filtered = filtered.map(region => ({
        ...region,
        countries: region.countries
          .map(country => {
            const normalizedCountryName = normalizeString(country.name);
            const countryMatches = normalizedCountryName.includes(normalizedQuery);
            
            // If country name matches, show all codes (or empty array if no codes)
            if (countryMatches) {
              return {
                ...country,
                codes: country.codes
              };
            }
            
            // Otherwise, filter codes by search query
            const filteredCodes = country.codes.filter(
              plate => 
                normalizeString(plate.code).includes(normalizedQuery) ||
                normalizeString(plate.location).includes(normalizedQuery)
            );
            
            // Return country if it has matching codes
            return {
              ...country,
              codes: filteredCodes
            };
          })
          .filter(country => {
            // Show country if: name matches OR has matching codes
            const normalizedCountryName = normalizeString(country.name);
            const countryMatches = normalizedCountryName.includes(normalizedQuery);
            return countryMatches || country.codes.length > 0;
          })
      })).filter(region => region.countries.length > 0);
    }

    return filtered;
  }, [searchQuery, selectedRegion]);

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  const allRegions = licensePlateData.map(r => r.name);

  return (
    <div className="w-full">
      <div className="mb-8 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-foreground">
          Guia: Matrícules
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground font-light">
          Codis de matrícula de cada país i el seu significat.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="mb-8 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Cerca per codi o localització..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 text-base border-2 border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all font-light placeholder:text-muted-foreground hover:border-muted-foreground"
          />
        </div>

        {/* Region Filter */}
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            onClick={() => setSelectedRegion(null)}
            className={`px-4 py-2 text-sm rounded-xl font-medium transition-all ${
              selectedRegion === null
                ? "bg-foreground text-background"
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
            }`}
          >
            Totes les regions
          </button>
          {allRegions.map(region => (
            <button
              key={region}
              onClick={() => setSelectedRegion(region)}
              className={`px-4 py-2 text-sm rounded-xl font-medium transition-all ${
                selectedRegion === region
                  ? "bg-foreground text-background"
                  : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
              }`}
            >
              {region}
            </button>
          ))}
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div className="text-center py-20 bg-secondary rounded-2xl border-2 border-border">
          <div className="text-5xl sm:text-6xl mb-4">🔍</div>
          <p className="text-base sm:text-lg text-foreground mb-2 font-semibold">
            No s'han trobat resultats
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Prova amb una altra cerca
          </p>
        </div>
      ) : (
        <div className="space-y-12">
          {filteredData.map((region) => (
          <div key={region.name} className="space-y-6">
            {/* Region Title */}
            <div className="border-b-2 border-border pb-3">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <span className="text-3xl">
                  {region.name === "Amèrica del Nord" && "🌎"}
                  {region.name === "Europa" && "🇪🇺"}
                  {region.name === "Àsia" && "🌏"}
                  {region.name === "Amèrica del Sud" && "🌎"}
                  {region.name === "Oceania" && "🌏"}
                  {region.name === "Àfrica" && "🌍"}
                  {region.name === "Orient Mitjà" && "🕌"}
                </span>
                {region.name}
              </h2>
            </div>

            {/* Countries */}
            {region.countries.map((country) => (
              <div key={country.countryCode} className="bg-card rounded-2xl border-2 border-border overflow-hidden">
                {/* Country Header */}
                <div className="bg-gradient-to-r from-secondary to-muted px-6 py-4 border-b-2 border-border">
                  <h3 className="text-xl font-bold text-foreground flex items-center gap-3">
                    <span className="px-3 py-1 bg-foreground text-background rounded-lg text-sm font-mono">
                      {country.countryCode}
                    </span>
                    {country.name}
                    <span className="text-2xl">{getCountryFlag(country.name)}</span>
                  </h3>
                </div>

                {/* Plate Format Example */}
                {plateFormats[country.name] && (
                  <div className="px-6 py-4 bg-secondary border-b border-border">
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-muted-foreground mb-2">Format de matrícula:</p>
                      <div className="inline-flex items-center gap-1 px-4 py-2 bg-card border-2 border-border rounded-lg font-mono text-lg font-bold">
                        {(() => {
                          const format = plateFormats[country.name];
                          const example = format.example;
                          const legendColors = format.legend;
                          
                          // Split by | to get colored parts
                          const parts = example.split('|');
                          
                          return parts.map((part, idx) => {
                            const colorClass = legendColors[idx]?.color || legendColors[legendColors.length - 1]?.color || "bg-gray-100 text-gray-800";
                            
                            // Special handling for EU band
                            if (part === "EU" && isEUCountry(country.name)) {
                              const countryLetter = euCountries[country.name];
                              return (
                                <span
                                  key={idx}
                                  className="px-1.5 py-2 rounded-l bg-blue-600 text-white flex items-center justify-center font-bold text-sm min-w-[24px]"
                                  title="Banda blava de la Unió Europea"
                                >
                                  {countryLetter}
                                </span>
                              );
                            }
                            
                            return (
                              <span
                                key={idx}
                                className={`px-2 py-1 rounded ${colorClass}`}
                              >
                                {part}
                              </span>
                            );
                          });
                        })()}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {plateFormats[country.name].legend.map((item, idx) => (
                        <span
                          key={idx}
                          className={`px-3 py-1 rounded-lg text-xs font-medium ${item.color}`}
                        >
                          {item.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Codes Table */}
                {country.codes.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-6">
                      {country.codes.map((plate, index) => (
                        <div
                          key={`${plate.code}-${index}`}
                          className="flex items-center justify-between p-4 bg-secondary rounded-xl hover:bg-secondary/80 transition-colors border border-border group cursor-pointer"
                          onClick={() => handleCopyCode(plate.code)}
                          title="Clica per copiar el codi"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="inline-flex items-center px-2.5 py-1 bg-foreground text-background rounded-lg text-sm font-mono font-bold">
                                {plate.code}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyCode(plate.code);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
                                title="Copiar codi"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                                  <path d="M4 16c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2h8c1.1 0 2 .9 2 2"/>
                                </svg>
                              </button>
                            </div>
                            <p className="text-sm text-muted-foreground font-medium">
                              {plate.location}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {country.name === "Regne Unit" && (
                      <div className="px-6 py-4 bg-blue-50 border-t border-blue-200">
                        <p className="text-xs text-blue-800 leading-relaxed">
                          <strong>Nota:</strong> Les segones lletres i combinacions completes donen oficines específiques dins d'aquestes àrees.
                          Per exemple, "AB" indica una oficina específica dins de la zona "A" (Anglia).
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-6 py-8 text-center text-muted-foreground text-sm">
                    {country.name === "Espanya" 
                      ? "Espanya utilitza un únic codi 'E' per tot el país, sense codis específics per províncies."
                      : country.name === "Estats Units"
                      ? "No existeix un format únic nacional homogeni. Cada estat emet les seves pròpies plaques amb formats variats."
                      : country.name === "Canadà"
                      ? "No existeix un format únic nacional homogeni. Cada província emet les seves pròpies plaques amb formats variats."
                      : country.name === "Mèxic"
                      ? "No existeix un format únic nacional homogeni. Cada estat emet les seves pròpies plaques amb formats variats."
                      : country.name === "Colòmbia"
                      ? "Format amb tres lletres i tres números assignats seqüencialment. El nom del municipi d'expedició s'inclou a la placa."
                      : country.name === "Ciutat del Vaticà"
                      ? "Les matrícules utilitzen només números aleatoris."
                      : "Les matrícules utilitzen només números i lletres aleatòries."
                    }
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
        </div>
      )}
    </div>
  );
};
